import { useAuth0 } from "@auth0/auth0-react";
import classNames from "classnames";
import { useRef, useState } from "react";
import { Pimento } from "../../components/Icon/assets/Pimento.tsx";
import { PreserveSearchNavigate } from "../../components/Navigate/PreserveSearchNavigate.tsx";
import { Spinner } from "../../components/Spinner/Spinner.tsx";
import { useUpdateUser } from "../Home/HomeIndex/hooks/useUpdateUser.ts";
import { useUser } from "../hooks/useUser.ts";
import { CustomizationOnboardingForm } from "./components/CustomizationOnboardingForm.tsx";
import { LoadingPage } from "./components/LoadingPage.tsx";
import { PlanSelectionOnboardingForm } from "./components/PlanSelectionOnboardingForm.tsx";
import { UserInformationsOnboardingForm } from "./components/UserInformationsOnboardingForm.tsx";
import {
  ABOUT_YOU_STEP,
  CUSTOMIZATION_STEP,
  PLAN_SELECTION_STEP,
} from "./constants.ts";
import type { OnboardingStep } from "./types.ts";

export const UserOnboardingView = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [onboardingStep, setOnboardingStep] =
    useState<OnboardingStep>(ABOUT_YOU_STEP);
  const { user } = useUser();
  const { logout } = useAuth0();

  const redirectionUrl = useRef<string | null>(null);
  const { updateUser } = useUpdateUser();

  // XXX: If user is already onboarded, we automatically redirect to the home page. This is useful in case of race issues between user query invalidation and navigation.
  if (user?.onboarding_finished_at) return <PreserveSearchNavigate to="/" />;

  return user ? (
    <div
      className={classNames(
        "relative flex-col h-full items-center py-800 bg-surface-secondary-rest overflow-y-auto",
        onboardingStep === PLAN_SELECTION_STEP
          ? "gap-[calc(2vh)]"
          : "gap-[calc(20vh)]",
      )}
    >
      <div className="absolute top-800">
        <Pimento size={106} />
      </div>
      <div className="flex-fill flex-col-center">
        {isLoading ? (
          <LoadingPage
            onComplete={() => {
              updateUser({ onboarding_finished: true });
            }}
          />
        ) : (
          <>
            {onboardingStep === ABOUT_YOU_STEP && (
              <UserInformationsOnboardingForm
                onComplete={() => setOnboardingStep(CUSTOMIZATION_STEP)}
              />
            )}
            {onboardingStep === CUSTOMIZATION_STEP && (
              <CustomizationOnboardingForm
                onComplete={() => setOnboardingStep(PLAN_SELECTION_STEP)}
              />
            )}
            {onboardingStep === PLAN_SELECTION_STEP && (
              <PlanSelectionOnboardingForm
                onComplete={() => setIsLoading(true)}
                // XXX: If users try to subscribe we consider the onboarding as finished. If they cancel or complete the subscription checkout,
                // the loading page won't be shown as the onboarding is over. If we absolutely need to show the loading page, we should store in the database
                // the fact that the user has seen or not the loading page.
                onSubscriptionCheckoutRedirection={() => {
                  redirectionUrl.current = null;
                  updateUser({ onboarding_finished: true });
                }}
              />
            )}
          </>
        )}
      </div>
      <div className="absolute bottom-400 left-0 right-0 text-center">
        <span className="body-sm-default text-primary">
          Not the right account? You can{" "}
          <button
            onClick={() => logout({ returnTo: window.location.origin })}
            className="text-primary underline"
          >
            logout and use a different account.
          </button>{" "}
        </span>
      </div>
    </div>
  ) : (
    <Spinner />
  );
};
